// PayoutLimitsCard — per-merchant configurable payout limits.
//
// Read-only for merchant users (rendered with "Using platform defaults"
// caption when applicable). Edit affordance shown only to admins. The
// backend gates PATCH /v1/merchants/:id/payout-limits/:provider with
// RequireRole(admin) so the UI guard is just defense-in-depth.
//
// Money handling (CLAUDE.md rule 6): every *_minor field is bigint INR
// paise end-to-end. The form converts to/from major rupee units only at
// the input boundary via rupeesToPaiseLong (which accepts amounts up to
// ₹50 million unlike the existing rupeesToPaise that caps at 12 digits).
import { useEffect, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Modal } from "@/components/UI";
import { fmtCcyMinor } from "@/utils/format";
import { providerLabel } from "@/utils/providerLabel";
import { apiErrorMessage } from "@/utils/apiError";
import { usePayoutLimits } from "@/queries/usePayoutLimits";
import {
  updatePayoutLimits,
  type PayoutLimits,
  type UpdatePayoutLimitsPayload,
} from "@/api/payoutLimits";

// rupeesToPaiseLong parses a rupees string to bigint paise. Unlike the
// existing rupeesToPaise() in Payouts.tsx (capped at 12 digits to fit
// per-payout amounts), this accepts up to 15 digits so the daily-
// merchant cap (₹50,00,00,000 = 11 digits in paise) doesn't reject
// realistic platform-level configurations.
function rupeesToPaiseLong(input: string): bigint | null {
  const t = input.trim();
  if (!/^\d{1,15}$/.test(t)) return null;
  return BigInt(t) * 100n;
}

// paiseToRupeesString — inverse, integer division. Only used for
// populating the form when the modal opens; never for money math.
function paiseToRupeesString(p: bigint): string {
  return (p / 100n).toString();
}

interface PayoutLimitsCardProps {
  merchantId: number;
  /** Admins get the Edit affordance; merchants see read-only. */
  canEdit: boolean;
}

export function PayoutLimitsCard({ merchantId, canEdit }: PayoutLimitsCardProps) {
  const { data: limits = [], isLoading, isError } = usePayoutLimits(merchantId);
  const [editingProvider, setEditingProvider] = useState<string | null>(null);

  const editingRow = limits.find((l) => l.provider === editingProvider);

  return (
    <div className="card" style={{ marginTop: "var(--card-gap)" }}>
      <div className="card-head">
        <h3>Payout limits</h3>
        <div className="spacer" />
        <span className="dim" style={{ fontSize: 11 }}>
          {isLoading ? "Loading…" : `${limits.length} provider${limits.length === 1 ? "" : "s"}`}
        </span>
      </div>
      {isLoading ? (
        <div className="empty">
          <div className="dim" style={{ fontSize: 13 }}>
            Loading limits…
          </div>
        </div>
      ) : isError ? (
        <div className="empty">
          <p>Couldn't load payout limits.</p>
        </div>
      ) : limits.length === 0 ? (
        <div className="empty">
          <p>No limit configuration for this merchant yet.</p>
        </div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>Provider</th>
              <th className="num">Per-txn min</th>
              <th className="num">Per-txn max</th>
              <th className="num">Daily / client</th>
              <th className="num">Daily / merchant</th>
              <th className="num">Daily / account</th>
              <th>Source</th>
              {canEdit && <th />}
            </tr>
          </thead>
          <tbody>
            {limits.map((row) => (
              <tr key={row.provider}>
                <td>{providerLabel(row.provider)}</td>
                <td className="num mono">
                  {fmtCcyMinor(row.txn_min_minor, "INR")}
                </td>
                <td className="num mono">
                  {fmtCcyMinor(row.txn_max_minor, "INR")}
                </td>
                <td className="num mono">
                  {fmtCcyMinor(row.daily_client_minor, "INR")}
                </td>
                <td className="num mono">
                  {fmtCcyMinor(row.daily_merchant_minor, "INR")}
                </td>
                <td className="num mono">
                  {fmtCcyMinor(row.daily_account_minor, "INR")}
                </td>
                <td>
                  <SourceBadge source={row.source} />
                </td>
                {canEdit && (
                  <td className="right">
                    <button
                      className="btn ghost sm"
                      type="button"
                      onClick={() => setEditingProvider(row.provider)}
                    >
                      <I.Edit size={11} /> Edit
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {canEdit && editingRow && (
        <EditLimitsModal
          merchantId={merchantId}
          row={editingRow}
          open
          onClose={() => setEditingProvider(null)}
        />
      )}
    </div>
  );
}

// ─── SourceBadge ─────────────────────────────────────────────────────────────

function SourceBadge({ source }: { source: PayoutLimits["source"] }) {
  // Custom override = visually emphasised; platform/compiled = dim.
  // No <Pill> wrapper — the dim style is a single-line caption, not a
  // status pill.
  if (source === "merchant") {
    return (
      <span
        style={{
          fontSize: 11,
          color: "var(--accent)",
          fontWeight: 500,
        }}
      >
        Custom
      </span>
    );
  }
  return (
    <span className="dim" style={{ fontSize: 11 }}>
      {source === "platform" ? "Platform default" : "Compiled default"}
    </span>
  );
}

// ─── EditLimitsModal ─────────────────────────────────────────────────────────

function EditLimitsModal({
  merchantId,
  row,
  open,
  onClose,
}: {
  merchantId: number;
  row: PayoutLimits;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [txnMin, setTxnMin] = useState(() => paiseToRupeesString(row.txn_min_minor));
  const [txnMax, setTxnMax] = useState(() => paiseToRupeesString(row.txn_max_minor));
  const [dailyClient, setDailyClient] = useState(() => paiseToRupeesString(row.daily_client_minor));
  const [dailyMerchant, setDailyMerchant] = useState(() => paiseToRupeesString(row.daily_merchant_minor));
  const [dailyAccount, setDailyAccount] = useState(() => paiseToRupeesString(row.daily_account_minor));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed when the modal (re)opens with a different row.
  useEffect(() => {
    if (open) {
      setTxnMin(paiseToRupeesString(row.txn_min_minor));
      setTxnMax(paiseToRupeesString(row.txn_max_minor));
      setDailyClient(paiseToRupeesString(row.daily_client_minor));
      setDailyMerchant(paiseToRupeesString(row.daily_merchant_minor));
      setDailyAccount(paiseToRupeesString(row.daily_account_minor));
      setSaving(false);
      setError(null);
    }
  }, [open, row]);

  const txnMinPaise = rupeesToPaiseLong(txnMin);
  const txnMaxPaise = rupeesToPaiseLong(txnMax);
  const dailyClientPaise = rupeesToPaiseLong(dailyClient);
  const dailyMerchantPaise = rupeesToPaiseLong(dailyMerchant);
  const dailyAccountPaise = rupeesToPaiseLong(dailyAccount);

  // Inline validation — backend has the authoritative CHECK constraints
  // but pre-flighting here keeps the operator's flow tight (no roundtrip
  // for an obvious miss).
  const parseErrors: string[] = [];
  if (txnMinPaise === null) parseErrors.push("Per-txn minimum must be a non-negative whole-rupee amount.");
  if (txnMaxPaise === null || txnMaxPaise === 0n) parseErrors.push("Per-txn maximum must be a positive whole-rupee amount.");
  if (dailyClientPaise === null || dailyClientPaise === 0n) parseErrors.push("Daily per-client cap must be positive.");
  if (dailyMerchantPaise === null || dailyMerchantPaise === 0n) parseErrors.push("Daily per-merchant cap must be positive.");
  if (dailyAccountPaise === null || dailyAccountPaise === 0n) parseErrors.push("Daily per-account cap must be positive.");
  if (
    txnMinPaise !== null &&
    txnMaxPaise !== null &&
    txnMinPaise > txnMaxPaise
  ) {
    parseErrors.push("Per-txn minimum can't be higher than the maximum.");
  }

  const canSubmit = !saving && parseErrors.length === 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (
      txnMinPaise === null ||
      txnMaxPaise === null ||
      dailyClientPaise === null ||
      dailyMerchantPaise === null ||
      dailyAccountPaise === null
    ) {
      return; // canSubmit gates this anyway
    }
    setSaving(true);
    setError(null);
    try {
      const payload: UpdatePayoutLimitsPayload = {
        txn_min_minor: txnMinPaise,
        txn_max_minor: txnMaxPaise,
        daily_client_minor: dailyClientPaise,
        daily_merchant_minor: dailyMerchantPaise,
        daily_account_minor: dailyAccountPaise,
      };
      await updatePayoutLimits(merchantId, row.provider, payload);
      await qc.invalidateQueries({ queryKey: ["payout-limits", merchantId] });
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not save the new limits."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={saving}
      title={`Edit limits — ${providerLabel(row.provider)}`}
      foot={
        <>
          <button
            className="btn ghost"
            type="button"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn accent"
            type="submit"
            form="edit-limits-form"
            disabled={!canSubmit}
          >
            {saving ? "Saving…" : "Save limits"}
          </button>
        </>
      }
    >
      <form id="edit-limits-form" onSubmit={handleSubmit} className="stack-16">
        <div className="dim" style={{ fontSize: 12 }}>
          All values in INR rupees. Daily aggregates apply over a rolling
          24-hour window. Failed payouts do not count toward the caps.
        </div>

        <LimitField
          label="Per-transaction minimum"
          help="Smallest single payout allowed for this merchant + provider."
          value={txnMin}
          onChange={setTxnMin}
          paise={txnMinPaise}
        />
        <LimitField
          label="Per-transaction maximum"
          help="Largest single payout allowed. Must be ≥ minimum."
          value={txnMax}
          onChange={setTxnMax}
          paise={txnMaxPaise}
        />
        <LimitField
          label="Daily volume per client (beneficiary)"
          help="Rolling 24-hour cap on the sum of payouts to one beneficiary."
          value={dailyClient}
          onChange={setDailyClient}
          paise={dailyClientPaise}
        />
        <LimitField
          label="Daily volume per merchant"
          help="Rolling 24-hour cap on the sum of all this merchant's payouts."
          value={dailyMerchant}
          onChange={setDailyMerchant}
          paise={dailyMerchantPaise}
        />
        <LimitField
          label="Daily settlement per account"
          help="Rolling 24-hour cap on the sum of payouts routed through one bank rail (may be shared by multiple merchants in future)."
          value={dailyAccount}
          onChange={setDailyAccount}
          paise={dailyAccountPaise}
        />

        {parseErrors.length > 0 && (
          <div style={{ color: "var(--warn, #b42318)", fontSize: 12 }}>
            {parseErrors.join(" ")}
          </div>
        )}
        {error && (
          <div style={{ color: "var(--warn, #b42318)", fontSize: 12 }}>
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

function LimitField({
  label,
  help,
  value,
  onChange,
  paise,
}: {
  label: string;
  help: string;
  value: string;
  onChange: (v: string) => void;
  /** null when the input doesn't parse. The dim caption below the
      input echoes the paise equivalent for sanity. */
  paise: bigint | null;
}) {
  return (
    <div className="field">
      <label className="label">{label}</label>
      <input
        className="input"
        type="text"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="off"
      />
      <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
        {help}
        {paise !== null && paise >= 0n && (
          <>
            {" · "}
            <span className="mono">{fmtCcyMinor(paise, "INR")}</span>
          </>
        )}
      </div>
    </div>
  );
}
